import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, IconButton, Button,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress, Tooltip, Menu, MenuItem, Alert, Stack, Grid,
  Avatar, Divider, Badge, Tabs, Tab, InputAdornment, Select,
  FormControl, InputLabel, List, ListItem, ListItemText, ListItemAvatar,
  Skeleton, CardActions, CardHeader, TextField
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  CheckCircle as CompletedIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  MoreVert as MoreIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingIcon,
  Assignment as AssignmentIcon,
  Link as LinkIcon,
  ErrorOutline as ErrorIcon,
  Close as CloseIcon,
  Description as TemplateIcon
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import client from '../../API';
import { statusChipProps } from './contractUtils';

const MyAssignmentsSection = () => {
  const [assignments, setAssignments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAssignment, setMenuAssignment] = useState(null);
  const [tabValue, setTabValue] = useState(0); // 0 = All, 1 = Regular, 2 = External

  // Pagination & Filters
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        rows: rowsPerPage,
        include_external: true
      };

      const response = await client.contracts.user.getMyAssignments(params);
      setAssignments(response.assignments || []);
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await client.contracts.user.getMyAssignmentStats();
      setStats(response.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchAssignments();
    fetchStats();
  }, [page]);

  const handleViewDetails = async (assignment) => {
    try {
      if (assignment.assignment_type === 'external') {
        const response = await client.contracts.user.getMyExternalAssignmentDetail(assignment.id);
        setSelectedAssignment(response.external_signature);
      } else {
        const response = await client.contracts.user.getMyAssignmentDetail(assignment.id);
        setSelectedAssignment(response.assignment);
      }
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Failed to load assignment details:', error);
      alert('Failed to load assignment details');
    }
  };

  const handleSendReminders = async (assignmentId) => {
    if (!confirm('Send reminder notifications to all pending recipients?')) return;

    try {
      const response = await client.contracts.user.remindAssignment(assignmentId);
      alert(response.message);
      fetchAssignments();
    } catch (error) {
      console.error('Failed to send reminders:', error);
      alert(error.response?.data?.error || 'Failed to send reminders');
    }
  };

  const handleCancelAssignment = async (assignment) => {
    if (!confirm('Cancel this assignment? All pending signatures will be cancelled.')) return;

    try {
      if (assignment.assignment_type === 'external') {
        await client.contracts.user.cancelMyExternalAssignment(assignment.id);
      } else {
        await client.contracts.user.cancelMyAssignment(assignment.id);
      }
      alert('Assignment cancelled successfully');
      fetchAssignments();
      fetchStats();
    } catch (error) {
      console.error('Failed to cancel assignment:', error);
      alert(error.response?.data?.error || 'Failed to cancel assignment');
    }
  };

  const handleMenuOpen = (event, assignment) => {
    setAnchorEl(event.currentTarget);
    setMenuAssignment(assignment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAssignment(null);
  };

  const getFilteredAssignments = () => {
    let filtered = assignments;

    // Tab filter
    if (tabValue === 1) {
      filtered = filtered.filter(a => a.assignment_type === 'regular');
    } else if (tabValue === 2) {
      filtered = filtered.filter(a => a.assignment_type === 'external');
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(a =>
        a.template?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.signer_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.signer_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => {
        if (statusFilter === 'pending') {
          return a.signature_stats?.pending > 0;
        } else if (statusFilter === 'completed') {
          return a.signature_stats?.pending === 0 && a.signature_stats?.signed > 0;
        } else if (statusFilter === 'declined') {
          return a.signature_stats?.declined > 0;
        }
        return true;
      });
    }

    return filtered;
  };

  const getScopeIcon = (scope) => {
    switch (scope) {
      case 'company': return <BusinessIcon fontSize="small" />;
      case 'team': return <GroupIcon fontSize="small" />;
      case 'user': return <PersonIcon fontSize="small" />;
      case 'creator': return <PersonIcon fontSize="small" />;
      default: return <GroupIcon fontSize="small" />;
    }
  };

  const getProgressColor = (stats) => {
    if (!stats?.total) return 'default';
    const completion = (stats.signed / stats.total) * 100;
    if (completion >= 75) return 'success';
    if (completion >= 50) return 'info';
    if (completion >= 25) return 'warning';
    return 'error';
  };

  const filteredAssignments = getFilteredAssignments();

  if (loading && assignments.length === 0) {
    return (
      <Box p={3}>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
          {[1, 2, 3].map(i => (
            <Grid item xs={12} key={i}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Enhanced Stats Cards */}
      {stats && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0}
              sx={{ 
                background: `linear-gradient(135deg, ${alpha('#2196f3', 0.15)} 0%, ${alpha('#2196f3', 0.05)} 100%)`,
                borderLeft: 4,
                borderColor: 'primary.main',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" fontWeight={700} color="primary.main">
                      {stats.total_all_signatures || stats.regular?.total_assignments || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight={500}>
                      Total Assignments
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                    <AssignmentIcon fontSize="large" />
                  </Avatar>
                </Stack>
                <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                  <Chip 
                    label={`${stats.regular?.total_assignments || 0} Regular`} 
                    size="small" 
                    sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600 }}
                  />
                  <Chip 
                    label={`${stats.external?.total_external_signatures || 0} External`} 
                    size="small"
                    sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 600 }}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0}
              sx={{ 
                background: `linear-gradient(135deg, ${alpha('#ff9800', 0.15)} 0%, ${alpha('#ff9800', 0.05)} 100%)`,
                borderLeft: 4,
                borderColor: 'warning.main',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" fontWeight={700} color="warning.main">
                      {stats.total_all_pending || stats.regular?.pending_signatures || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight={500}>
                      Pending Signatures
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                    <PendingIcon fontSize="large" />
                  </Avatar>
                </Stack>
                <Typography variant="caption" color="text.secondary" mt={2} display="block">
                  Awaiting signature from recipients
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0}
              sx={{ 
                background: `linear-gradient(135deg, ${alpha('#4caf50', 0.15)} 0%, ${alpha('#4caf50', 0.05)} 100%)`,
                borderLeft: 4,
                borderColor: 'success.main',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" fontWeight={700} color="success.main">
                      {stats.total_all_signed || stats.regular?.signed_signatures || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight={500}>
                      Completed
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                    <CompletedIcon fontSize="large" />
                  </Avatar>
                </Stack>
                <Typography variant="caption" color="text.secondary" mt={2} display="block">
                  Successfully signed contracts
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0}
              sx={{ 
                background: `linear-gradient(135deg, ${alpha('#9c27b0', 0.15)} 0%, ${alpha('#9c27b0', 0.05)} 100%)`,
                borderLeft: 4,
                borderColor: 'secondary.main',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" fontWeight={700} color="secondary.main">
                      {stats.overall_completion_rate || stats.regular?.completion_rate || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight={500}>
                      Completion Rate
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}>
                    <TrendingIcon fontSize="large" />
                  </Avatar>
                </Stack>
                <Typography variant="caption" color="text.secondary" mt={2} display="block">
                  Overall signature completion
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content Card */}
      <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
        {/* Header with Tabs and Actions */}
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <AssignmentIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  My Contract Assignments
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Manage and track all your contract assignments
                </Typography>
              </Box>
            </Stack>
          }
          action={
            <Tooltip title="Refresh">
              <IconButton onClick={fetchAssignments} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          }
          sx={{ pb: 0 }}
        />

        <Divider />

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, v) => setTabValue(v)}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                fontSize: '0.95rem'
              }
            }}
          >
            <Tab 
              label={
                <Badge badgeContent={assignments.length} color="primary" max={999}>
                  <Box px={2}>All Assignments</Box>
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge 
                  badgeContent={assignments.filter(a => a.assignment_type === 'regular').length} 
                  color="primary"
                  max={999}
                >
                  <Box px={2}>Regular</Box>
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge 
                  badgeContent={assignments.filter(a => a.assignment_type === 'external').length} 
                  color="secondary"
                  max={999}
                >
                  <Box px={2}>External</Box>
                </Badge>
              } 
            />
          </Tabs>
        </Box>

        <CardContent sx={{ p: 3 }}>
          {/* Filters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
            <TextField
              size="small"
              placeholder="Search by template, email, or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PendingIcon fontSize="small" color="warning" />
                    <span>Pending</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="completed">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompletedIcon fontSize="small" color="success" />
                    <span>Completed</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="declined">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CancelIcon fontSize="small" color="error" />
                    <span>Declined</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Assignments Grid */}
          {filteredAssignments.length === 0 ? (
            <Paper 
              elevation={0}
              sx={{ 
                p: 8, 
                textAlign: 'center',
                bgcolor: alpha('#2196f3', 0.03),
                border: 2,
                borderStyle: 'dashed',
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <AssignmentIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom fontWeight={600}>
                No Assignments Found
              </Typography>
              <Typography variant="body2" color="text.disabled" mb={3}>
                {searchQuery || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Create a template and assign it to users to get started!'
                }
              </Typography>
              {!searchQuery && statusFilter === 'all' && (
                <Button variant="contained" startIcon={<TemplateIcon />}>
                  Create Your First Template
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={2.5}>
              {filteredAssignments.map((assignment) => (
                <Grid item xs={12} key={`${assignment.assignment_type}-${assignment.id}`}>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      '&:hover': { 
                        boxShadow: 4,
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      borderWidth: 2,
                      borderRadius: 2
                    }}
                  >
                    <CardContent sx={{ pb: 1.5 }}>
                      <Stack spacing={2.5}>
                        {/* Header Row */}
                        <Stack 
                          direction="row" 
                          justifyContent="space-between" 
                          alignItems="flex-start"
                          spacing={2}
                        >
                          <Box flexGrow={1}>
                            <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5} flexWrap="wrap">
                              <Avatar 
                                sx={{ 
                                  bgcolor: assignment.assignment_type === 'external' 
                                    ? 'secondary.main' 
                                    : 'primary.main',
                                  width: 40,
                                  height: 40
                                }}
                              >
                                <TemplateIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="h6" fontWeight={700}>
                                  {assignment.template?.name || 'Unknown Template'}
                                </Typography>
                                <Stack direction="row" spacing={1} mt={0.5}>
                                  <Chip 
                                    label={assignment.assignment_type || 'regular'}
                                    size="small"
                                    color={assignment.assignment_type === 'external' ? 'secondary' : 'primary'}
                                    sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                                  />
                                  {assignment.assignment_scope && (
                                    <Chip
                                      icon={getScopeIcon(assignment.assignment_scope)}
                                      label={assignment.assignment_scope}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Stack>
                              </Box>
                            </Stack>
                            
                            {assignment.assignment_message && (
                              <Alert 
                                severity="info" 
                                sx={{ 
                                  py: 0.5,
                                  '& .MuiAlert-message': {
                                    fontSize: '0.875rem'
                                  }
                                }}
                              >
                                {assignment.assignment_message}
                              </Alert>
                            )}
                          </Box>

                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, assignment)}
                            sx={{ 
                              '&:hover': { 
                                bgcolor: 'action.hover',
                                transform: 'scale(1.1)'
                              }
                            }}
                          >
                            <MoreIcon />
                          </IconButton>
                        </Stack>

                        {/* Info Chips */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                          {assignment.assignment_type === 'external' && (
                            <Chip
                              icon={<EmailIcon fontSize="small" />}
                              label={assignment.signer_email}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          )}
                          <Chip
                            icon={<CalendarIcon fontSize="small" />}
                            label={`Created ${new Date(assignment.created_at).toLocaleDateString()}`}
                            size="small"
                            variant="outlined"
                          />
                          {assignment.due_date && (
                            <Chip
                              icon={<CalendarIcon fontSize="small" />}
                              label={`Due ${new Date(assignment.due_date).toLocaleDateString()}`}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Stack>

                        {/* Progress Section */}
                        <Box>
                          <Stack 
                            direction="row" 
                            justifyContent="space-between" 
                            alignItems="center"
                            mb={1}
                          >
                            <Typography variant="body2" fontWeight={600} color="text.secondary">
                              Signature Progress
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {assignment.signature_stats?.signed || 0}/{assignment.signature_stats?.total || 0} Signed
                              <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                                ({Math.round(((assignment.signature_stats?.signed || 0) / (assignment.signature_stats?.total || 1)) * 100)}%)
                              </Typography>
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={((assignment.signature_stats?.signed || 0) / (assignment.signature_stats?.total || 1)) * 100}
                            color={getProgressColor(assignment.signature_stats)}
                            sx={{ 
                              height: 10, 
                              borderRadius: 1,
                              bgcolor: alpha('#000', 0.05)
                            }}
                          />
                        </Box>

                        {/* Status Badges */}
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
                          {(assignment.signature_stats?.pending || 0) > 0 && (
                            <Chip
                              icon={<PendingIcon />}
                              label={`${assignment.signature_stats.pending} Pending`}
                              size="small"
                              color="warning"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                          {(assignment.signature_stats?.signed || 0) > 0 && (
                            <Chip
                              icon={<CompletedIcon />}
                              label={`${assignment.signature_stats.signed} Signed`}
                              size="small"
                              color="success"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                          {(assignment.signature_stats?.declined || 0) > 0 && (
                            <Chip
                              icon={<CancelIcon />}
                              label={`${assignment.signature_stats.declined} Declined`}
                              size="small"
                              color="error"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                          {(assignment.signature_stats?.expired || 0) > 0 && (
                            <Chip
                              icon={<ErrorIcon />}
                              label={`${assignment.signature_stats.expired} Expired`}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>

                    <Divider />

                    <CardActions sx={{ px: 2.5, py: 1.5, bgcolor: alpha('#000', 0.01) }}>
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewDetails(assignment)}
                        sx={{ fontWeight: 600 }}
                      >
                        View Details
                      </Button>
                      {assignment.assignment_type === 'regular' && (assignment.signature_stats?.pending || 0) > 0 && (
                        <Button
                          size="small"
                          startIcon={<SendIcon />}
                          onClick={() => handleSendReminders(assignment.id)}
                          color="warning"
                          sx={{ fontWeight: 600 }}
                        >
                          Send Reminders
                        </Button>
                      )}
                      {assignment.assignment_type === 'external' && assignment.public_url && (
                        <Button
                          size="small"
                          startIcon={<LinkIcon />}
                          onClick={() => {
                            navigator.clipboard.writeText(assignment.public_url);
                            alert('Link copied to clipboard!');
                          }}
                          sx={{ fontWeight: 600 }}
                        >
                          Copy Link
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 200,
            mt: 1
          }
        }}
      >
        <MenuItem 
          onClick={() => {
            handleViewDetails(menuAssignment);
            handleMenuClose();
          }}
          sx={{ py: 1.5 }}
        >
          <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: 'primary.main' }}>
            <ViewIcon fontSize="small" />
          </Avatar>
          <Typography fontWeight={500}>View Details</Typography>
        </MenuItem>
        {menuAssignment?.assignment_type === 'regular' && (menuAssignment?.signature_stats?.pending || 0) > 0 && (
          <MenuItem 
            onClick={() => {
              handleSendReminders(menuAssignment.id);
              handleMenuClose();
            }}
            sx={{ py: 1.5 }}
          >
            <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: 'warning.main' }}>
              <SendIcon fontSize="small" />
            </Avatar>
            <Typography fontWeight={500}>Send Reminders</Typography>
          </MenuItem>
        )}
        {menuAssignment?.assignment_type === 'external' && menuAssignment?.public_url && (
          <MenuItem 
            onClick={() => {
              navigator.clipboard.writeText(menuAssignment.public_url);
              alert('Link copied!');
              handleMenuClose();
            }}
            sx={{ py: 1.5 }}
          >
            <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: 'info.main' }}>
              <LinkIcon fontSize="small" />
            </Avatar>
            <Typography fontWeight={500}>Copy Public Link</Typography>
          </MenuItem>
        )}
        <Divider sx={{ my: 1 }} />
        <MenuItem 
          onClick={() => {
            handleCancelAssignment(menuAssignment);
            handleMenuClose();
          }}
          sx={{ py: 1.5 }}
        >
          <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </Avatar>
          <Typography color="error" fontWeight={600}>Cancel Assignment</Typography>
        </MenuItem>
      </Menu>

      {/* Enhanced Assignment Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                <AssignmentIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Assignment Details
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Complete contract information
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={() => setDetailDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 3 }}>
          {selectedAssignment && (
            <Stack spacing={3}>
              {/* Template Info */}
              <Paper sx={{ p: 3, bgcolor: alpha('#2196f3', 0.05), borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {selectedAssignment.template?.name}
                </Typography>
                
                {selectedAssignment.assignment_message && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {selectedAssignment.assignment_message}
                  </Alert>
                )}

                <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" gap={1}>
                  {selectedAssignment.created_at && (
                    <Chip
                      icon={<CalendarIcon fontSize="small" />}
                      label={`Created: ${new Date(selectedAssignment.created_at).toLocaleDateString()}`}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  {selectedAssignment.due_date && (
                    <Chip
                      icon={<CalendarIcon fontSize="small" />}
                      label={`Due: ${new Date(selectedAssignment.due_date).toLocaleDateString()}`}
                      size="small"
                      color="warning"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </Paper>

              {/* Signatures List */}
              {selectedAssignment.signatures && selectedAssignment.signatures.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom mb={2}>
                    Signatures ({selectedAssignment.signatures.length})
                  </Typography>
                  <List sx={{ bgcolor: 'background.paper' }}>
                    {selectedAssignment.signatures.map((sig, idx) => (
                      <React.Fragment key={sig.id}>
                        {idx > 0 && <Divider />}
                        <ListItem
                          sx={{
                            py: 2,
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: sig.status === 'signed' 
                                  ? 'success.main' 
                                  : sig.status === 'declined' 
                                  ? 'error.main' 
                                  : 'warning.main',
                                width: 48,
                                height: 48
                              }}
                            >
                              {sig.status === 'signed' ? (
                                <CompletedIcon fontSize="large" />
                              ) : sig.status === 'declined' ? (
                                <CancelIcon fontSize="large" />
                              ) : (
                                <PendingIcon fontSize="large" />
                              )}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body1" fontWeight={600}>
                                {sig.signer_name || 'Unknown Signer'}
                              </Typography>
                            }
                            secondary={
                              <Stack spacing={1} mt={1}>
                                {sig.signer_email && (
                                  <Typography variant="body2" color="text.secondary">
                                    <EmailIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                    {sig.signer_email}
                                  </Typography>
                                )}
                                <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                                  <Chip
                                    {...statusChipProps(sig.status)}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                  <Chip
                                    label={`Created ${new Date(sig.created_at).toLocaleDateString()}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                  {sig.signed_at && (
                                    <Chip
                                      icon={<CompletedIcon fontSize="small" />}
                                      label={`Signed ${new Date(sig.signed_at).toLocaleDateString()}`}
                                      size="small"
                                      color="success"
                                      sx={{ fontWeight: 600 }}
                                    />
                                  )}
                                </Stack>
                              </Stack>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}

              {/* External Signature Details */}
              {selectedAssignment.signer_email && (
                <Paper sx={{ p: 3, bgcolor: alpha('#9c27b0', 0.05), borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom mb={2}>
                    External Signer Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Name</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedAssignment.signer_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedAssignment.signer_email}
                      </Typography>
                    </Grid>
                    {selectedAssignment.signer_phone && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedAssignment.signer_phone}
                        </Typography>
                      </Grid>
                    )}
                    {selectedAssignment.creator_username && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Creator Username</Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedAssignment.creator_username}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Chip
                        {...statusChipProps(selectedAssignment.status)}
                        sx={{ mt: 1, fontWeight: 600 }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Stack>
          )}
        </DialogContent>
        
        <Divider />
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailDialogOpen(false)} variant="outlined" size="large">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyAssignmentsSection;